import {
  SkeletonContent,
  SkeletonLine,
  Typography,
} from '@one-thd/sui-atomic-components';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ActiveOrders } from '@thd-olt-component-react/active-orders';
import { useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosError } from 'axios';
import React, { useEffect, useState, JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { quickbooksSync } from '../api/oms/order-history';
import { BopisOutOfStockRecoveryAlert } from '../components/purchase-history/bopis-out-of-stock-recovery-alert/BopisOutOfStockRecoveryAlert';
import { BuyItAgain } from '../components/purchase-history/buy-it-again/BuyItAgain';
import { CardAndTableView } from '../components/purchase-history/CardAndTableView';
import { CPSAnnouncementBanner } from '../components/purchase-history/cps-announcement-banner/CPSAnnouncementBanner';
import { MissingPurchase } from '../components/purchase-history/MissingPurchase';
import { OrderFilter } from '../components/purchase-history/order-filter/OrderFilter';
import { DateFilter } from '../components/purchase-history/order-filter/DateFilter';
import { OrderSearch } from '../components/purchase-history/order-search/OrderSearch';
import { OrderViewToggle } from '../components/purchase-history/order-view-toggle/OrderViewToggle';
import { AddReceiptButton } from '../components/purchase-history/page-header/AddReceiptButton';
import { OrderHistoryHeader } from '../components/purchase-history/page-header/OrderHistoryHeader';
import { PTCInfoBanner } from '../components/purchase-history/ptc-info-banner/PTCInfoBanner';
import { ReorderRecommendationsSection } from '../components/purchase-history/reorder-recommendations/ReorderRecommendationsSection';
import { SortAndSearch } from '../components/purchase-history/sort-and-search/index';
import { PHTableSettingsTile } from '../components/purchase-history/table-view/index';
import { AddItemsFakeDoor } from '../components/purchase-history/AddItemsFakeDoor';
import { AlertBanner } from '../components/shared/alert-banner/AlertBanner';
import PageLayout from '../components/shared/PageLayout';
import { CommonProvider } from '../contexts/index';
import { PurchaseHistoryProvider } from '../contexts/PurchaseHistoryProvider';
import { PHXSatisfactionSurvey } from '../components/shared/PHXSatisfactionSurvey';
import { SpendOverview } from '../components/total-perks-spend/SpendOverview';
import { FeatureSwitches } from '../constants/feature-switches';
import { OrderHistoryModalHooks } from '../hooks/purchase-history-hooks';
import {
  useCommonContext,
  useExperienceContext,
  usePurchaseHistoryContext,
} from '../hooks/useContexts';
import { IAlert } from '../models/common';
import { IOrder, IQuickbooksSyncRequest } from '../models/purchase-history';
import { getWindow } from '../utils/general';
import {
  setPurchaseHistoryPageAnalytics,
  setQBSyncError,
} from '../utils/purchase-history/analytics';

import '../components/purchase-history/table-view/ButtonGroup.scss';
import '../styles/order-history.scss';

interface PurchaseHistoryProps {
  customerType: 'b2b' | 'b2c';
  customerRole: string;
}

// TODO: Remove /* eslint-disable max-lines */ from all files 🚨

/**
 * @param alertState
 * @param qbAlertState
 * @returns Element that holds the alert banners for the purchase history page
 */
const AlertBanners = (
  alertState: IAlert | null,
  qbAlertState: IAlert | null,
): JSX.Element => (
  <>
    {(alertState || qbAlertState) && (
      <div className="sui-col-span-1 sui-max-w-[1039px]">
        <AlertBanner
          headline={alertState?.alertTitle || qbAlertState?.alertTitle || ''}
          status={alertState?.status || qbAlertState?.status || 'info'}
          onClose={alertState?.onClose || qbAlertState?.onClose}
        >
          {alertState?.alertMessage || qbAlertState?.alertMessage}
        </AlertBanner>
      </div>
    )}
  </>
);

const PurchaseHistoryContent = ({
  customerType,
  customerRole,
}: PurchaseHistoryProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useCommonContext();
  const {
    alertState,
    setAlertState,
    orderHistoryData,
    orderHistoryError,
    orderHistoryLoaded,
    viewType,
    pHRequest,
    cpsDataSource,
    allOrderDetails,
  } = usePurchaseHistoryContext();

  const { svocID, userID } = useThdCustomer() ?? {};
  const { isConsumerApp } = useExperienceContext();
  const sectionRowClassName = `sui-col-span-1 lg:sui-flex lg:sui-gap-[31px]
                ${
                  isConsumerApp ? '' : 'sui-mb-[24px] md:sui-mb-0'
                } sui-max-w-[1039px]`;

  const crumbs = useAccountCrumbs([
    {
      label: 'Purchase History',
      path: '/myaccount/purchase-history',
    },
  ]);

  const orderHistoryModalHooks = OrderHistoryModalHooks();

  const [enableSettingsDrawer, setEnableSettingsDrawer] = useState(false);
  const [qbAlertState, setQBAlertState] = useState<IAlert | null>(null);
  const [searchText, setSearchText] = useState<string>('');

  useEffect(() => {
    setPurchaseHistoryPageAnalytics();

    const authCode = searchParams.get('qb_auth_code');
    const realmId = searchParams.get('qb_realm_id');
    const qbState = searchParams.get('qb_state');
    const qbCancel = searchParams.get('cancel');
    if (authCode && realmId && qbState) {
      const request: IQuickbooksSyncRequest = {
        quickBooksSyncRequest: {
          qbState,
          qbRealmId: realmId,
        },
      };
      quickbooksSync(request, authCode, userID)
        .then(() => {
          // set successful banner
          setQBAlertState({
            alertMessage:
              'Your Quickbooks account has been successfully synced.',
            status: 'success',
            onClose: () => setQBAlertState(null),
          });
        })
        .catch((error) => {
          // set error banner
          setQBSyncError(error);
          setQBAlertState({
            alertMessage:
              'There was an issue syncing your Quickbooks account, please try again.',
            status: 'error',
          });
        })
        .finally(() => {
          searchParams.delete('qb_auth_code');
          searchParams.delete('qb_realm_id');
          searchParams.delete('qb_state');
          setSearchParams(searchParams);
        });
    }
    if (qbCancel) {
      searchParams.delete('cancel');
      setSearchParams(searchParams);
    }
  }, []);

  const poUpdatedOrderHistory: string | null =
    getWindow()?.sessionStorage.getItem('purchaseHistory-poJobUpdate') || null;

  /**
   * TBD - ? what is this for
   */
  if (orderHistoryData?.orders && poUpdatedOrderHistory) {
    const parsedPOUpdatedOrderHistory: IOrder[] = JSON.parse(
      poUpdatedOrderHistory,
    );

    for (let i = 0; i < parsedPOUpdatedOrderHistory?.length; i += 1) {
      for (let _j = 0; _j < orderHistoryData.orders.length; _j += 1) {
        if (
          parsedPOUpdatedOrderHistory?.[i]?.orderNumbers?.[0] ===
            orderHistoryData?.orders?.[_j]?.orderNumbers?.[0] &&
          parsedPOUpdatedOrderHistory?.[i]?.totalAmount ===
            orderHistoryData?.orders?.[_j]?.totalAmount
        ) {
          orderHistoryData.orders[_j] = parsedPOUpdatedOrderHistory[i];
        }
      }
    }
  }

  useEffect(() => {
    if (orderHistoryData) {
      getWindow()?.sessionStorage.setItem(
        'orderHistory',
        JSON.stringify(orderHistoryData),
      );
    }
  }, [orderHistoryLoaded, orderHistoryData]);

  useEffect(() => {
    if ((orderHistoryError as AxiosError)?.response?.status === 500) {
      setAlertState({
        alertTitle: 'Purchase History Unavailable',
        alertMessage:
          "We're sorry, there is a technical error. Please try again later.",
        status: 'error',
      });
    }
  }, [orderHistoryError]);

  const searchByObj = pHRequest.searchBy?.[0] ?? {};
  const isLineItemData = Object.keys(searchByObj)?.[0] === 'itemDescription';
  const showActiveOrders = FeatureSwitches.enableActiveOrders && isMobile;
  const showCPSSearch =
    viewType !== 'LocalDeliveries' &&
    FeatureSwitches.enableCPSSearch &&
    cpsDataSource === 'CPS' &&
    orderHistoryLoaded;
  const activeOrders = (
    <ErrorBoundary name="active-orders">
      <ActiveOrders componentLocation="purchase-history" />
    </ErrorBoundary>
  );

  return (
    <PageLayout
      crumbs={crumbs}
      customerRole={customerRole}
      customerType={customerType}
      headerComponent={<OrderHistoryHeader />}
      internalRoutes={['/myaccount/order-history']}
      isMobile={isMobile}
      pageTitle="Purchase History"
    >
      <>
        <div className="sui-flex sui-flex-col sui-w-full sui-gap-3 sui-max-w-[1039px]">
          {customerType === 'b2b' && cpsDataSource === 'CPS' && (
            <CPSAnnouncementBanner />
          )}
          {FeatureSwitches.enableBopisOutOfStockRecoverability && (
            <BopisOutOfStockRecoveryAlert allOrderDetails={allOrderDetails} />
          )}
        </div>
        <AddItemsFakeDoor orderDetail={allOrderDetails?.[0]} />
        <div className="sui-isolation-auto sui-max-w-[1039px]">
          <PHXSatisfactionSurvey />
        </div>
        {(orderHistoryData?.orderCount || 0) > 1 && customerType === 'b2b' && (
          <div className="sui-flex sui-flex-col sui-w-full sui-mb-6 sui-max-w-[1039px]">
            <ErrorBoundary name="ptc-info-banner">
              <QueryProvider cacheKey="loyalty-benefits-cacheKey">
                <PTCInfoBanner />
              </QueryProvider>
            </ErrorBoundary>
          </div>
        )}
        <div className="sui-grid sui-grid-cols-1 md:sui-gap-6 sui-max-w-[1039px]">
          {customerType === 'b2b' && (
            <div className={sectionRowClassName}>
              <ErrorBoundary name="buy-it-again">
                <BuyItAgain />
              </ErrorBoundary>

              <ErrorBoundary name="spend-overview">
                <SpendOverview />
              </ErrorBoundary>
            </div>
          )}
          {customerType === 'b2c' &&
            FeatureSwitches.enableB2CReorderRecommendations && (
              <div className={sectionRowClassName}>
                <ErrorBoundary name="reorder-recommendations">
                  <QueryProvider cacheKey="reorder-recommendations-cacheKey">
                    <ReorderRecommendationsSection />
                  </QueryProvider>
                </ErrorBoundary>
              </div>
            )}
          {viewType !== 'activeOrders' ? (
            <div className="no-print sui-col-span-1 sui-max-w-[1039px]">
              <div className="sui-flex sui-flex-row">
                <ErrorBoundary name="sort-and-search">
                  {(() => {
                    if (showCPSSearch) {
                      return (
                        <div className="sui-flex sui-flex-col sui-gap-5 sui-w-full md:sui-mb-0">
                          <div className="sui-flex sui-w-full sui-flex-col md:sui-flex-row sui-gap-3 sui-grow">
                            <DateFilter />
                            <div className="sui-flex sui-flex-row sui-grow">
                              <OrderSearch
                                searchText={searchText}
                                setSearchText={setSearchText}
                              />
                              <OrderFilter />
                            </div>
                          </div>
                          {showActiveOrders && activeOrders}
                          {isMobile && (
                            <div className="sui-mb-[16px] sui-grid sui-grid-cols-2 sui-w-full">
                              <Typography variant="h4"> All Orders </Typography>
                              <div className="sui-text-end">
                                <AddReceiptButton
                                  svocId={svocID}
                                  display="link"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    if (orderHistoryLoaded) {
                      return <SortAndSearch />;
                    }
                    return (
                      <div className="sui-flex sui-w-full">
                        <SkeletonContent initialSize="full" disablePadding="x">
                          <SkeletonLine variant="heading" fullWidth />
                        </SkeletonContent>
                      </div>
                    );
                  })()}
                </ErrorBoundary>
                {customerType === 'b2c' && !isMobile && (
                  <div className="sui-hidden md:sui-flex md:sui-self-center md:sui-pt-6">
                    <MissingPurchase />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div />
          )}
          {showActiveOrders && !showCPSSearch && (
            <div className="sui-col-span-1 sui-max-w-[1039px]">
              {activeOrders}
            </div>
          )}
          <div className="no-print sui-hidden md:sui-flex md:sui-items-center lg:sui-flex lg:sui-items-center sui-col-span-1 sui-justify-between sui-max-w-[1039px]">
            <div className="sui-w-full sui-flex sui-justify-between">
              {customerType !== 'b2c' && (
                <>
                  <div className="sui-flex sui-items-center">
                    <ErrorBoundary name="order-view-toggle">
                      <OrderViewToggle />
                    </ErrorBoundary>
                    {!isMobile && <MissingPurchase />}
                  </div>
                  {viewType === 'Table' && !isLineItemData && (
                    <ErrorBoundary name="table-customization">
                      <PHTableSettingsTile
                        setEnableSettingsDrawer={setEnableSettingsDrawer}
                      />
                    </ErrorBoundary>
                  )}
                </>
              )}
            </div>
          </div>
          {AlertBanners(alertState, qbAlertState)}
          <div className="sui-col-span-1 sui-max-w-[1039px]">
            <CardAndTableView
              orderHistoryModalHooks={orderHistoryModalHooks}
              enableSettingsDrawer={enableSettingsDrawer}
              setEnableSettingsDrawer={setEnableSettingsDrawer}
            />
          </div>

          {orderHistoryModalHooks.orderHistoryModalComponent}
        </div>
      </>
    </PageLayout>
  );
};

export const PurchaseHistory = ({
  customerType,
  customerRole,
}: PurchaseHistoryProps) => (
  <CommonProvider>
    <PurchaseHistoryProvider
      customerType={customerType}
      customerRole={customerRole}
    >
      <PurchaseHistoryContent
        customerType={customerType}
        customerRole={customerRole}
      />
    </PurchaseHistoryProvider>
  </CommonProvider>
);

PurchaseHistory.displayName = 'Purchase History';
